/** @jsxImportSource @emotion/react */
import { Link } from 'react-router-dom';
import React, { useMemo, useState } from 'react';
import { Breadcrumb, Button, Card, Form, Input, InputNumber, Modal, Spin, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { commonStyles } from '../../styles/commonStyles';
import MainLayout from '../../components/common/MainLayout';
import {
  useGetPendingUsersQuery,
  useUpdatePendingUserStatusMutation,
  useGetUsersQuery,
  useUpdateUserMaxFarmsMutation,
  type PendingUser,
  type AdminUser,
} from '../../api/adminApi';
import { toastError, toastSuccess } from '../../utils/toast';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

interface StatusModalState {
  id: string | number;
  currentStatus?: string | null;
}

interface MaxFarmsModalState {
  id: string | number;
  currentValue?: number | null;
}

const statusColorMap: Record<string, string> = {
  approved: 'green',
  active: 'green',
  rejected: 'red',
  declined: 'red',
  pending: 'gold',
  invited: 'blue',
  inactive: 'default',
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const idx = value.indexOf('T');
  return idx > 0 ? value.slice(0, idx) : value;
};

const renderStatusTag = (status?: string | null) => {
  if (!status) return null;
  const normalized = status.toLowerCase();
  const color = statusColorMap[normalized] || 'default';
  return <Tag color={color}>{status}</Tag>;
};

const AdminUsersPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const user =useSelector((state: any) => state.auth.user);
  const userActions = useSelector((state: any) => state.auth.user?.actions);
  
  const canManageUsers = useMemo(() => {
    if (user && typeof userActions === 'object' && 'global' in userActions) {
      return (
        user.actions?.global?.farms?.includes('users.update') ||
        user.actions?.global?.farms?.includes('users.manage') ||
        ['admin', 'superadmin', 'superuser'].includes(String(user.role).toLowerCase())
      );
    }

    return Array.isArray(userActions) && userActions.includes('users.update');
  }, [user, userActions]);

  const { data: pendingUsers, isLoading: pendingLoading, isError: pendingError } = useGetPendingUsersQuery(undefined, {
    skip: !canManageUsers,
  });
  const { data: allUsers, isLoading: usersLoading, isError: usersError } = useGetUsersQuery(undefined, {
    skip: !canManageUsers,
  });
  const [updateStatus, { isLoading: updatingStatus }] = useUpdatePendingUserStatusMutation();
  const [updateMaxFarms, { isLoading: updatingMaxFarms }] = useUpdateUserMaxFarmsMutation();

  const [statusModal, setStatusModal] = useState<StatusModalState | null>(null);
  const [form] = Form.useForm<{ status: string }>();
  const [maxFarmsModal, setMaxFarmsModal] = useState<MaxFarmsModalState | null>(null);
  const [maxFarmsForm] = Form.useForm<{ max_farms: number }>();

  const openStatusModal = (user: PendingUser) => {
    setStatusModal({ id: user.id, currentStatus: user.status });
    form.setFieldsValue({ status: user.status || '' });
  };

  const handleStatusSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!statusModal) return;
      await updateStatus({ id: statusModal.id, status: values.status }).unwrap();
      toastSuccess(t('users.messages.statusUpdated'));
      setStatusModal(null);
      form.resetFields();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const openMaxFarmsModal = (user: AdminUser) => {
    setMaxFarmsModal({ id: user.id, currentValue: user.max_farms ?? undefined });
    maxFarmsForm.setFieldsValue({ max_farms: user.max_farms ?? 0 });
  };

  const handleMaxFarmsSubmit = async () => {
    try {
      const values = await maxFarmsForm.validateFields();
      if (!maxFarmsModal) return;
      await updateMaxFarms({ id: maxFarmsModal.id, max_farms: values.max_farms }).unwrap();
      toastSuccess(t('users.messages.maxFarmsUpdated'));
      setMaxFarmsModal(null);
      maxFarmsForm.resetFields();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const pendingColumns: ColumnsType<PendingUser> = [
    {
      title: t('users.columns.name'),
      dataIndex: 'username',
      render: (value, record) =>
        value || record.name || record.email || record.phone || record.id,
    },
    { title: t('users.columns.email'), dataIndex: 'email' },
    { title: t('users.columns.phone'), dataIndex: 'phone' },
    {
      title: t('common:labels.status'),
      dataIndex: 'status',
      render: (value) => renderStatusTag(value),
    },
    {
      title: t('users.columns.createdAt'),
      dataIndex: 'created_at',
      render: formatDate,
    },
    {
      title: t('common:labels.actions'),
      render: (_, record) => (
        <Button type="link" onClick={() => openStatusModal(record)}>
          {t('users.actions.changeStatus')}
        </Button>
      ),
    },
  ];

  const usersColumns: ColumnsType<AdminUser> = [
    {
      title: t('users.columns.name'),
      dataIndex: 'username',
      render: (value, record) => value || record.name || record.email || record.phone || record.id,
    },
    { title: t('users.columns.email'), dataIndex: 'email' },
    { title: t('users.columns.phone'), dataIndex: 'phone' },
    {
      title: t('common:labels.status'),
      dataIndex: 'status',
      render: (value) => renderStatusTag(value),
    },
    { title: t('users.columns.role'), dataIndex: 'role' },
    {
      title: t('users.columns.maxFarms'),
      dataIndex: 'max_farms',
      render: (value: number | null | undefined) =>
        value !== undefined && value !== null ? value : '—',
    },
    {
      title: t('users.columns.createdAt'),
      dataIndex: 'created_at',
      render: formatDate,
    },
    {
      title: t('users.columns.updatedAt'),
      dataIndex: 'updated_at',
      render: formatDate,
    },
    {
      title: t('common:labels.actions'),
      render: (_, record) => (
        <>
          {String(user?.role).toLowerCase() === 'superuser' && <Link to={`/admin/users/${record.id}/saves`} style={{ marginRight: 12 }}>{t('inspection.open')}</Link>}
          <Button type="link" onClick={() => openMaxFarmsModal(record)}>
            {t('users.actions.changeMaxFarms')}
          </Button>
        </>
      ),
    },
  ];

  if (!canManageUsers) {
    return (
      <MainLayout
        breadcrumb={
          <Breadcrumb
            items={[
              { title: t('users.breadcrumb.admin') },
              { title: t('users.title') },
            ]}
          />
        }
      >
        <Card>
          <Card.Meta title={t('users.noAccess.title')} description={t('users.noAccess.description')} />
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: t('users.breadcrumb.admin') },
            { title: t('users.title') },
          ]}
        />
      }
    >
      <Tabs
        defaultActiveKey="pending"
        items={[
          {
            key: 'pending',
            label: t('users.tabs.pending'),
            children: (
              <Spin spinning={pendingLoading}>
                {pendingError ? (
                  <Card>
                    <Card.Meta title={t('users.errors.title')} description={t('users.errors.pendingLoadFailed')} />
                  </Card>
                ) : (
                  <Table
                    rowKey={(user) => String(user.id)}
                    dataSource={pendingUsers || []}
                    columns={pendingColumns}
                    pagination={{ pageSize: 10 }}
                  />
                )}
              </Spin>
            ),
          },
          {
            key: 'all',
            label: t('users.tabs.all'),
            children: (
              <Spin spinning={usersLoading}>
                {usersError ? (
                  <Card>
                    <Card.Meta title={t('users.errors.title')} description={t('users.errors.usersLoadFailed')} />
                  </Card>
                ) : (
                  <Table
                    rowKey={(user) => String(user.id)}
                    dataSource={allUsers || []}
                    columns={usersColumns}
                    pagination={{ pageSize: 10 }}
                  />
                )}
              </Spin>
            ),
          },
        ]}
      />

      <Modal
        title={t('users.statusModal.title')}
        open={!!statusModal}
        onCancel={() => {
          setStatusModal(null);
          form.resetFields();
        }}
        onOk={handleStatusSubmit}
        confirmLoading={updatingStatus}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t('common:labels.status')}
            name="status"
            rules={[{ required: true, message: t('users.statusModal.required') }]}
            initialValue={statusModal?.currentStatus || ''}
          >
            <Input placeholder={t('users.statusModal.placeholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('users.maxFarmsModal.title')}
        open={!!maxFarmsModal}
        onCancel={() => {
          setMaxFarmsModal(null);
          maxFarmsForm.resetFields();
        }}
        onOk={handleMaxFarmsSubmit}
        confirmLoading={updatingMaxFarms}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
      >
        <Form form={maxFarmsForm} layout="vertical">
          <Form.Item
            label={t('users.maxFarmsModal.label')}
            name="max_farms"
            rules={[{ required: true, message: t('users.maxFarmsModal.required') }]}
            initialValue={maxFarmsModal?.currentValue ?? 0}
          >
            <InputNumber min={0} css={commonStyles.fullWidth} />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default AdminUsersPage;
